import { createSlice } from '@reduxjs/toolkit';
import { GetTimelines } from './thunks/get-timeline-templates.thunk';
import { GetTimelineStages } from './thunks/get-timeline-stages.thunk';
import { TimelinesState } from './timeline.state';
import { GetEngagemets } from './thunks/get-timeline-engagements.thunk';
import { GetTimelineById } from './thunks/get-timeline-by-id.thunk';
import { GetDocumentTemplates } from './thunks/get-document-templates.thunk';
import { GetSampleTemplates } from './thunks/get-sample-templates.thunk';
import { GetBuyerNdaTemplates } from './thunks/get-buyer-nda-templates.thunk';
import { GetEngagementDocuments } from './thunks/get-engagement-documents.thunk';
import { GetCurrentEvents } from './thunks/get-current-events.thunk';
import { UpdateUseCreditsForMonthlyFees } from './thunks/update-use-credits-for-monthly-fees.thunk';
import { GetEngagementAdvisorCompanies } from './thunks/get-engagement-advisor-companies.thunk';
import { UpdateSellerDaqiEditingDuringSuspension } from './thunks/update-seller-daqi-editing-during-suspension.thunk';

const initialState: TimelinesState = {
    engagementTimelinesTable: {
        timelines: [],
        pageNumber: 0,
        totalItems: 0,
        pageSize: 0,
    },
    timelineTemplatesTable: {
        timelines: [],
        pageNumber: 0,
        totalItems: 0,
        pageSize: 0,
    },
    engagementDocumentsTables: {},
    currentTimeline: undefined,
    currentTimelineEvents: {
        events: [],
        pageNumber: 0,
        totalItems: 0,
        pageSize: 0,
    },
    documentTemplatesTable: {
        templates: [],
        pageNumber: 0,
        totalItems: 0,
        pageSize: 0,
    },
    sampleTemplatesTable: {
        templates: [],
        pageNumber: 0,
        totalItems: 0,
        pageSize: 0,
    },
    buyerNdaTemplatesTable: {
        templates: [],
        pageNumber: 0,
        totalItems: 0,
        pageSize: 0,
    },
    timelineStages: [],
    engagementAdvisorCompanies: [],
    isLoading: false,
    status: 'succeeded',
    error: '',
};

const timelinesSlice = createSlice({
    name: 'timelines',
    initialState,
    reducers: {
        resetCurrentTimeline(state) {
            state.currentTimeline = undefined;
        },
        udateCurrentTimelineDocumentSections(state, action) {
            state.currentTimeline.engagementData.documentSectionsList =
                action.payload;
        },
        addEngagementObserver(state, action: { payload: { engagementId: number; observer: { id: number; brokerId: number; brokerName: string } } }) {
            const timeline = state.engagementTimelinesTable.timelines.find(
                (t) => t.id === action.payload.engagementId
            );
            if (timeline?.engagementProcess) {
                if (!timeline.engagementProcess.observers) {
                    timeline.engagementProcess.observers = [];
                }
                timeline.engagementProcess.observers.push(action.payload.observer);
            }
        },
        removeEngagementObserver(state, action: { payload: { engagementId: number; brokerId: number } }) {
            const timeline = state.engagementTimelinesTable.timelines.find(
                (t) => t.id === action.payload.engagementId
            );
            if (timeline?.engagementProcess?.observers) {
                timeline.engagementProcess.observers = timeline.engagementProcess.observers.filter(
                    (o) => o.brokerId !== action.payload.brokerId
                );
            }
        },
        setIsWalletCreditsForMonthlyFeesAllowed(state, action: { payload: boolean }) {
            if (!state.currentTimeline?.engagementProcess) return;
            state.currentTimeline.engagementProcess.isWalletCreditsForMonthlyFeesAllowed =
                action.payload;
            if (!action.payload) {
                state.currentTimeline.engagementProcess.useCreditsForMonthlyFees =
                    false;
            }
        },
    },
    extraReducers(builder) {
        builder
            .addCase(GetTimelines.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetTimelines.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetTimelines.fulfilled, (state, action) => {
                const { timelineTemplates, pageNumber, totalItems, pageSize } =
                    action.payload;
                state.timelineTemplatesTable.timelines = timelineTemplates;
                state.timelineTemplatesTable.pageNumber = pageNumber;
                state.timelineTemplatesTable.totalItems = totalItems;
                state.timelineTemplatesTable.pageSize = pageSize;
                setSucceededState(state);
            })
            .addCase(GetTimelineById.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetTimelineById.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetTimelineById.fulfilled, (state, action) => {
                state.currentTimeline = action.payload;
                setSucceededState(state);
            })
            .addCase(GetTimelineStages.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetTimelineStages.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetTimelineStages.fulfilled, (state, action) => {
                state.timelineStages = action.payload;
                setSucceededState(state);
            })
            .addCase(GetEngagemets.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetEngagemets.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetEngagemets.fulfilled, (state, action) => {
                const { timelines, pageNumber, totalItems, pageSize } =
                    action.payload;
                state.engagementTimelinesTable.timelines = timelines;
                state.engagementTimelinesTable.pageNumber = pageNumber;
                state.engagementTimelinesTable.totalItems = totalItems;
                state.engagementTimelinesTable.pageSize = pageSize;

                setSucceededState(state);
            })
            .addCase(GetDocumentTemplates.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetDocumentTemplates.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetDocumentTemplates.fulfilled, (state, action) => {
                const { templates, pageNumber, totalItems, pageSize } =
                    action.payload;
                state.documentTemplatesTable.templates = templates;
                state.documentTemplatesTable.pageNumber = pageNumber;
                state.documentTemplatesTable.totalItems = totalItems;
                state.documentTemplatesTable.pageSize = pageSize;

                setSucceededState(state);
            })
            .addCase(GetEngagementDocuments.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetEngagementDocuments.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetEngagementDocuments.fulfilled, (state, action) => {
                const {
                    sectionId,
                    engagementDocuments,
                    pageNumber,
                    totalItems,
                    pageSize,
                } = action.payload;
                state.engagementDocumentsTables[sectionId] = {
                    engagementDocuments,
                    pageNumber,
                    totalItems,
                    pageSize,
                };

                setSucceededState(state);
            })
            .addCase(GetCurrentEvents.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetCurrentEvents.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetCurrentEvents.fulfilled, (state, action) => {
                const { events, pageNumber, totalItems, pageSize } =
                    action.payload;
                state.currentTimelineEvents.events = events;
                state.currentTimelineEvents.pageNumber = pageNumber;
                state.currentTimelineEvents.totalItems = totalItems;
                state.currentTimelineEvents.pageSize = pageSize;
                setSucceededState(state);
            })
            .addCase(GetSampleTemplates.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetSampleTemplates.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetSampleTemplates.fulfilled, (state, action) => {
                const { templates, pageNumber, totalItems, pageSize } =
                    action.payload;
                state.sampleTemplatesTable.templates = templates;
                state.sampleTemplatesTable.pageNumber = pageNumber;
                state.sampleTemplatesTable.totalItems = totalItems;
                state.sampleTemplatesTable.pageSize = pageSize;
                setSucceededState(state);
            })
            .addCase(GetBuyerNdaTemplates.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetBuyerNdaTemplates.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetBuyerNdaTemplates.fulfilled, (state, action) => {
                const { templates, pageNumber, totalItems, pageSize } =
                    action.payload;
                state.buyerNdaTemplatesTable.templates = templates;
                state.buyerNdaTemplatesTable.pageNumber = pageNumber;
                state.buyerNdaTemplatesTable.totalItems = totalItems;
                state.buyerNdaTemplatesTable.pageSize = pageSize;
                setSucceededState(state);
            })
            .addCase(UpdateUseCreditsForMonthlyFees.fulfilled, (state, action) => {
                if (state.currentTimeline?.engagementProcess) {
                    state.currentTimeline.engagementProcess.useCreditsForMonthlyFees =
                        action.payload.useCreditsForMonthlyFees;
                }
            })
            .addCase(
                UpdateSellerDaqiEditingDuringSuspension.fulfilled,
                (state, action) => {
                    if (state.currentTimeline?.engagementProcess) {
                        state.currentTimeline.engagementProcess.isSellerDaqiEditableDuringSuspension =
                            action.payload.isEnabled;
                    }
                }
            )
            .addCase(GetEngagementAdvisorCompanies.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetEngagementAdvisorCompanies.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetEngagementAdvisorCompanies.fulfilled, (state, action) => {
                state.engagementAdvisorCompanies = action.payload.advisorCompanies;
                setSucceededState(state);
            });
    },
});

const setRejectedState = (state: any) => {
    state.isLoading = false;
    state.status = 'failed';
    state.error = state;
};

const setPendingState = (state: any) => {
    state.isLoading = true;
    state.status = 'loading';
    state.error = '';
};

const setSucceededState = (state: any) => {
    state.isLoading = false;
    state.status = 'succeeded';
    state.error = '';
};

export const { resetCurrentTimeline, udateCurrentTimelineDocumentSections, addEngagementObserver, removeEngagementObserver, setIsWalletCreditsForMonthlyFeesAllowed } =
    timelinesSlice.actions;
export default timelinesSlice.reducer;
